import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
    Entity,
    UILayout,
    UIElementType,
    Description,
    RecordGroup,
} from '#types/System.types';
import { RecordStatus } from '#types/System.Parameters.types';
import { useLayoutEditorBase } from './useLayoutEditorBase';
import { useGet } from '../data/useGet';
import { useAdd } from '../data/useAdd';
import { useSet } from '../data/useSet';
import { useArchive } from '../archive/useArchive';
import { createCollectionStore } from '../../store';

const entitiesStore = createCollectionStore<Entity>('entities');

/**
 * Custom hook for managing entity layout editing functionality.
 * Provides state management and operations for entity layouts including:
 * - Entity selection and creation
 * - Layout modifications
 * - Description updates
 * - Save operations
 * - Entity deletion
 * 
 * @returns {Object} Entity editor state and methods
 * @property {Entity | null} structure - Currently selected entity structure
 * @property {Entity[]} structures - List of available entities
 * @property {string | undefined} selectedStructureId - ID of the currently selected structure
 * @property {Entity[]} entities - Alias for structures, list of available entities
 */
export const useLayoutEditorEntity = () => {
    const [selectedStructureId, setSelectedStructureId] = useState<string | undefined>();
    const [structure, setStructure] = useState<Entity | null>(null);
    const { get } = useGet();
    const { add } = useAdd();
    const { archiveEntity } = useArchive();
    const { set } = useSet();
    
    useEffect(() => {
        entitiesStore.connect();
        return () => entitiesStore.disconnect();
    }, []);

    const entitiesSnap = useSnapshot(entitiesStore.state);
    const structures = useMemo<Entity[]>(() => {
        const result: Entity[] = [];
        for (const id of entitiesSnap.docIds) {
            const entry = entitiesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as Entity), docId: id });
        }
        return result;
    }, [entitiesSnap.docIds, entitiesSnap.docs]);

    const handleSave = useCallback(async (layout: UILayout, description: Description, state?: any) => {
        if (!structure || !selectedStructureId) {
            console.error('Save failed: No structure selected');
            throw new Error('No structure selected');
        }
        
        try {
            const updates: Omit<Entity, 'docId'> = {
                defaultUILayout: (
                    ({ meta, ...rest }) => rest)(layout as UILayout & { meta?: any }
                ),
                description,
                recordGroups: state?.recordGroups || {},
                status: structure.status
            };

            await set('entities', selectedStructureId, updates);
        } catch (error) {
            console.error('Error saving entity:', error);
            throw error;
        }
    }, [set, structure, selectedStructureId]);

    const baseEditor = useLayoutEditorBase({
        onSave: handleSave
    });

    const { 
        dispatch,
        layoutEditorState,
    } = baseEditor;

    /**
     * Updates a specific field in the structure description
     * @param {keyof Description} key - The description field to update
     * @param {string} value - New value for the field
     */
    const handleDescriptionChange = (key: keyof Description, value: string) => {
        dispatch({
            type: 'UPDATE_DESCRIPTION',
            payload: {
                [key]: value
            }
        });
    };

    /**
     * Selects a structure (Entity) for editing and loads its layout
     * @param {string} structureId - ID of the Entity to select
     * @returns {Promise<boolean>} Promise that resolves with true if selection was successful
     * @throws {Error} If structure cannot be loaded
     */
    const selectStructure = useCallback(async (structureId: string) => {
        try {
            const fetchedEntity = await get<Entity>('entities', structureId);
            if (!fetchedEntity) {
                console.error('Entity not found:', structureId);
                return false;
            }
            
            setSelectedStructureId(structureId);
            setStructure(fetchedEntity);
            dispatch({
                type: 'SELECT_STRUCTURE',
                payload: {
                    layout: fetchedEntity.defaultUILayout,
                    description: fetchedEntity.description,
                    recordGroups: fetchedEntity.recordGroups || {}
                }
            });
            return true;
        } catch (error) {
            console.error('Failed to select entity:', error);
            throw error;
        }
    }, [get, dispatch]);

    /**
     * Creates a new structure (Entity) with default layout and description
     * @returns {Promise<string>} Promise that resolves with the ID of the newly created Entity
     */
    const createStructure = useCallback(async () => {
        try {
            const newEntity: Omit<Entity, 'docId'> = {
                defaultUILayout: {
                    structure: {
                        uiElementType: UIElementType.Column,
                        children: []
                    }
                },
                description: {
                    shortLabel: 'New Record Type',
                    shortDescription: '',
                    longLabel: '',
                    longDescription: 'Recently created Record Type, please update this description.'
                },
                status: RecordStatus.Active
            };

            const newEntityId = await add('entities', newEntity);
            setSelectedStructureId(newEntityId);
            setStructure({ 
                ...newEntity, 
                docId: newEntityId 
            });
            dispatch({
                type: 'SELECT_STRUCTURE',
                payload: {
                    layout: newEntity.defaultUILayout,
                    description: newEntity.description,
                    recordGroups: {}
                }
            });
            return newEntityId;
        } catch (error) {
            console.error('Error creating entity:', error);
            throw error;
        }
    }, [add, dispatch]);

    /**
     * Archives a structure (Entity) and clears selection. Rejects with the
     * backend error so the caller can surface its reason.
     * @param {string} structureId - ID of the Entity to delete
     */
    const deleteStructure = useCallback(async (structureId: string) => {
        await archiveEntity(structureId);
        setSelectedStructureId(undefined);
        setStructure(null);
        dispatch({ type: 'RESET_LAYOUT' });
    }, [archiveEntity, dispatch]);

    /**
     * Clears the current structure selection and resets layout
     */
    const clearSelection = () => {
        setSelectedStructureId(undefined);
        setStructure(null);
        dispatch({ type: 'RESET_LAYOUT' });
    };

    /**
     * Updates the record groups for the current entity
     * This dispatches the UPDATE_RECORD_GROUPS action which is tracked in the undo/redo history
     * @param {Object} groups - New record groups object
     */
    const handleRecordGroupsChange = (groups: { [key: string]: RecordGroup }) => {
        dispatch({
            type: 'UPDATE_RECORD_GROUPS',
            payload: groups
        });
    };

    /**
     * Duplicates the current entity structure with a new name
     * @param {string} newName - Name for the duplicated entity
     * @returns {Promise<string>} Promise that resolves with the ID of the newly duplicated entity
     * @throws {Error} If no structure is selected or duplication fails
     */
    const duplicateStructure = useCallback(async (newName: string) => {
        if (!structure || !selectedStructureId) {
            throw new Error('No structure selected to duplicate');
        }

        try {
            const duplicatedEntity: Omit<Entity, 'docId'> = {
                defaultUILayout: (
                    ({ meta, ...rest }) => rest)(structure.defaultUILayout as UILayout & { meta?: any }
                ),
                description: {
                    ...structure.description,
                    shortLabel: newName,
                    longLabel: `${newName} (Copy of ${structure.description?.longLabel || structure.description?.shortLabel || 'Untitled'})`
                },
                recordGroups: structure.recordGroups || {},
                status: RecordStatus.Active
            };

            const newEntityId = await add('entities', duplicatedEntity);
            return newEntityId;
        } catch (error) {
            console.error('Error duplicating entity:', error);
            throw error;
        }
    }, [structure, selectedStructureId, add]);

    return {
        ...baseEditor,
        structure,
        structures,
        entities: structures,
        createStructure,
        deleteStructure,
        duplicateStructure,
        selectStructure,
        selectedStructureId,
        clearSelection,
        handleDescriptionChange,
        recordGroups: layoutEditorState.recordGroups || {},
        handleRecordGroupsChange,
    };
}; 